import { Grid, makeStyles, TextField, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import DatePicker from 'components/DatePicker';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import React from 'react';
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled';
import clsx from 'clsx';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import { format } from 'date-fns';
import styles from '../styles';

const useStyles = makeStyles(styles);

export interface RemoveSessionsFormDisplay {
  enabled: boolean;
  session_removal_time: string;
  session_removal_max_limit?: number | null | string;
  session_removal_message?: string;
}

export const removeSessionsInitialValues = {
  enabled: false,
  session_removal_time: '',
  session_removal_max_limit: null,
  session_removal_message: '',
};

export interface Props {
  defaultValue: RemoveSessionsFormDisplay | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  onChange: (values: RemoveSessionsFormDisplay) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const RemoveSessionsForm = ({
  defaultValue,
  role,
  companySelected,
  permissions,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();
  const [values, setValues] = React.useState<RemoveSessionsFormDisplay>(
    removeSessionsInitialValues
  );

  const handleChange = (
    newValues: RemoveSessionsFormDisplay,
    toggle?: boolean
  ) => {
    const updated =
      typeof toggle === 'boolean'
        ? { ...newValues, enabled: toggle }
        : newValues;
    setValues(updated);

    const payload: RemoveSessionsFormDisplay = { ...updated };

    onChange(payload);
  };

  React.useEffect(() => {
    if (defaultValue) {
      setValues(defaultValue);
    }
  }, [defaultValue]);

  React.useEffect(() => {
    setHasError(
      !!values?.enabled && values?.session_removal_time === 'Invalid Date'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <ToggleCard
      active={values.enabled}
      id="toggle-remove-sessions"
      title={i18n.t('REMOVE_USER_SESSIONS_KEY')}
      getActive={(value: boolean) => handleChange(values, !value)}
      icon={
        <PersonAddDisabledIcon
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      disable={
        !!permissions && !permissions?.edit_env_session_removal_instances
      }
    >
      <Grid container className={classes.FormContainer}>
        <Grid item className={classes.datePicker}>
          <DatePicker
            disabled={
              !!permissions && !permissions?.edit_env_session_removal_instances
            }
            id="run-time-remove-sessions"
            type="time"
            label={i18n.t('REMOVE_SESSIONS_TIME_KEY')}
            value={
              values?.session_removal_time
                ? `Wed Nov 22 2023 ${values?.session_removal_time}`
                : null
            }
            error={values?.session_removal_time === 'Invalid Date'}
            keyValue="session_removal_time"
            setFieldsValue={(key, value) => {
              if (
                typeof value === 'object' &&
                String(value) !== 'Invalid Date' &&
                value !== null
              ) {
                const date = new Date(String(value));

                const formattedDate = format(date, 'HH:mm:ss');
                handleChange(
                  { ...values, [key]: formattedDate },
                  values.enabled
                );
              }
              if (String(value) === 'Invalid Date' || value === null) {
                handleChange(
                  { ...values, [key]: 'Invalid Date' },
                  values.enabled
                );
              }
            }}
          />
        </Grid>

        <>
          <Grid item className={classes.sessionRemovalMaxLimit}>
            <TextField
              id="session-removal-max-limit"
              label={i18n.t('SESSION_REMOVAL_MAX_LIMIT_KEY')}
              type="number"
              inputProps={{ min: 0 }}
              variant="outlined"
              defaultValue={2}
              value={
                values.session_removal_max_limit === null ||
                typeof values.session_removal_max_limit === 'undefined'
                  ? ''
                  : String(values.session_removal_max_limit)
              }
              onChange={e => {
                const v = e.target.value;
                const parsed = v === '' ? null : Math.max(0, parseInt(v, 10));
                handleChange({
                  ...values,
                  session_removal_max_limit: parsed,
                });
              }}
              fullWidth
              disabled={
                !!permissions &&
                !permissions?.edit_env_session_removal_instances
              }
            />
            <Tooltip
              className={classes.tooltipTitle}
              placement="right"
              arrow
              title={i18n.t('REMOVE_SESSIONS_TOOLTIP_KEY')}
            >
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Grid>

          <Grid item className={classes.removalMessage}>
            <TextField
              id="session-removal-message"
              label={i18n.t('ADD_MESSAGE_REMOVE_SESSION_KEY')}
              value={values.session_removal_message ?? ''}
              variant="outlined"
              onChange={e =>
                handleChange({
                  ...values,
                  session_removal_message: e.target.value,
                })
              }
              fullWidth
              disabled={
                !!permissions &&
                !permissions?.edit_env_session_removal_instances
              }
              multiline
            />
          </Grid>
        </>
      </Grid>
    </ToggleCard>
  );
};

export default RemoveSessionsForm;
